import { Menu, PanelLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button, cn } from '@clarix/ui';
import { useSidebar } from './context/SidebarContext';
import { TopBarSlotTarget, useTopBarSlotFilled } from './TopBarAppSlots';

interface TopBarProps {
  /** Left-of-center chip (e.g. clario-frontend's MeetingDropdown). */
  meetingChip?: ReactNode;
  /** Right-of-center search box. Stays visible across apps. */
  search?: ReactNode;
  /** Timer pill, left of the search. */
  timer?: ReactNode;
  /** Bell + popover. */
  notifications?: ReactNode;
  /** Quick-action icons (create task, schedule meeting, Spark, ...). */
  quickActions?: ReactNode;
  /** Avatar trigger and dropdown. */
  userMenu?: ReactNode;
  /** The pinned panel is showing: start after rail + panel, since the panel is full height. */
  panelPinned?: boolean;
  /** Show the collapse/expand control for the pinned panel (off while the rail is hidden or inert). */
  panelToggle?: boolean;
}

/**
 * Exact-copy chrome from clario-frontend/src/components/layout/TopBar.tsx.
 *
 * - Fixed `top-0 right-0 z-50`, height 48px, white bg, bottom border; starts
 *   after the rail, or after rail + panel while the full-height panel is pinned.
 * - Mobile hamburger toggles the sidebar's mobile overlay state.
 * - Every feature is a slot. Slots receiving `null`/`undefined` collapse
 *   without affecting surrounding spacing. The shell composes which slots
 *   get real components; apps must not import this component directly.
 */
export function TopBar({
  meetingChip,
  search,
  timer,
  notifications,
  quickActions,
  userMenu,
  panelPinned = false,
  panelToggle = false,
}: TopBarProps) {
  const { toggleMobile, isMobileOpen, isPanelOpen, lastPanel, routePanel, togglePanel, closePanel, isResizing } =
    useSidebar();
  // A page with a side detail keeps this control after the panel is collapsed.
  // A page with no panel shows nothing here.
  const restorePanel = lastPanel ?? routePanel;
  const showToggle = panelToggle && (isPanelOpen || restorePanel !== null);
  // While the active app fills the notifications slot (the real popover with
  // its own data layer), the shell's fallback bell is suppressed.
  const appNotifications = useTopBarSlotFilled('notifications');

  return (
    <header
      className={cn(
        'border-border fixed left-16 right-0 top-0 z-50 flex h-12 min-h-[48px] items-center gap-2.5 border-b bg-white px-5 max-lg:left-0 lg:pr-[calc(1.25rem+var(--chat-compact-offset,0px))]',
        // Same timing as the content area's margin, so bar and page move together —
        // and, like it, no easing while the panel is being drag-resized.
        isResizing
          ? 'transition-[padding] duration-200'
          : 'transition-[left,padding] duration-300 ease-in-out',
        panelPinned && 'lg:left-[var(--sidebar-offset)]',
      )}
    >
      {/* Mobile hamburger */}
      <Button
        variant="ghost"
        size="icon"
        className="text-muted-foreground h-7 w-7 shrink-0 lg:hidden"
        onClick={toggleMobile}
        aria-label="Toggle navigation menu"
        aria-controls="sidebar-rail"
        aria-expanded={isMobileOpen}
      >
        <Menu className="h-4 w-4" strokeWidth={1.5} />
      </Button>

      {/* The panel's one collapse/expand control. It lives here, not in the panel
          or on the rail, so it stays put whichever state the panel is in. */}
      {showToggle && (
        <button
          type="button"
          className="-ml-2 hidden h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 lg:flex"
          onClick={() => {
            if (isPanelOpen) closePanel();
            else if (restorePanel) togglePanel(restorePanel);
          }}
          aria-label={isPanelOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={isPanelOpen}
        >
          <PanelLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
      )}

      {meetingChip}
      <TopBarSlotTarget slot="meeting-chip" />

      {/* Spacer */}
      <div className="flex-1" />

      {timer}
      <TopBarSlotTarget slot="timer" />

      {search && <div className="w-full max-w-md">{search}</div>}

      {(notifications || appNotifications) && (
        <div className="bg-border hidden h-[22px] w-px shrink-0 sm:block" />
      )}
      {!appNotifications && notifications}
      <TopBarSlotTarget slot="notifications" />

      {quickActions}
      <TopBarSlotTarget slot="quick-actions" />

      {userMenu && <div className="ml-1">{userMenu}</div>}

      {/* Last on the bar, after the avatar. Platform-level affordances live here
          rather than in `quick-actions`, which is the current app's own actions
          and so is a different width in every app — Spark would land in a
          different place in each one. */}
      <TopBarSlotTarget slot="trailing" />
    </header>
  );
}
